import React, { useState } from "react";
import { motion } from "framer-motion";
import { Clipboard, CheckCircle } from "lucide-react";
import { siteConfig } from '../constant/siteConfig';
import { useLanguage } from '../i18n/LanguageContext';

const BankDetails = ({ banksData }) => {
    const { t } = useLanguage();
    const bankKeys = Object.keys(banksData);
    const [activeBank, setActiveBank] = useState(bankKeys[0]);
    const [copiedKey, setCopiedKey] = useState(null);

    const resolveBankName = (name) => {
        const bankNameMap = {
            'USA Bank Account': t('bank.accounts.usa'),
            'EUR Bank Account': t('bank.accounts.eur'),
        };

        return bankNameMap[name] || name;
    };

    const resolveDetailKey = (key) => {
        const detailKeyMap = {
            'Beneficiary Name': t('bank.details.beneficiaryName'),
            'Account Number': t('bank.details.accountNumber'),
            'SWIFT / BIC Code': t('bank.details.swiftBicCode'),
            'ABA Routing Number': t('bank.details.abaRoutingNumber'),
            IBAN: t('bank.details.iban'),
            Currency: t('bank.details.currency'),
            'Bank Country': t('bank.details.bankCountry'),
            'Bank Name': t('bank.details.bankName'),
            'Bank Address': t('bank.details.bankAddress'),
            IMPORTANT: t('bank.details.important'),
        };

        return detailKeyMap[key] || key;
    };

    const resolveDetailValue = (key, value) => {
        if (key === 'IMPORTANT') {
            if (value === 'Only for payments in USD.') {
                return t('bank.details.importantUsd');
            }
            if (value === 'Only for EUR payments via SEPA.') {
                return t('bank.details.importantEur');
            }
        }

        return value;
    };

    const handleCopy = (key, text) => {
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    return (
        <div className="flex flex-col items-center w-full mx-auto p-4">
            {/* Bank Selection Tabs */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar px-2 py-2 bg-white rounded-full shadow-md w-full">
                {bankKeys.map((key) => (
                    <motion.button
                        key={key}
                        onClick={() => setActiveBank(key)}
                        whileTap={{ scale: 0.95 }}
                        className={`px-4 py-2 whitespace-nowrap rounded-full transition-all text-sm font-medium ${activeBank === key
                                ? "text-white shadow-md"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        style={
                            activeBank === key
                                ? {
                                    backgroundColor: siteConfig.brandColors.primary,
                                    color: siteConfig.brandColors.buttonText,
                                }
                                : undefined
                        }
                    >
                        {resolveBankName(banksData[key].name || key)}
                    </motion.button>
                ))}
            </div>

            {/* Bank Details Card */}
            <motion.div
                key={activeBank}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="w-full mt-4 p-5 bg-white rounded-lg shadow-lg"
            >
                {Object.entries(banksData[activeBank].details).map(([detailKey, value]) => (
                    <div
                        key={detailKey}
                        className="flex justify-between items-center py-3 border-b last:border-b-0"
                    >
                        <span className={` font-medium text-sm ${detailKey === 'IMPORTANT' ? 'text-red-500' :'text-gray-700'}`}>{resolveDetailKey(detailKey)}:</span>
                        <div className="flex items-center gap-2">
                            <span className={` text-sm ${detailKey === 'IMPORTANT' ? 'text-red-500' : 'text-gray-900'}`}>{resolveDetailValue(detailKey, value)}</span>
                            <motion.button
                                whileTap={{ scale: 0.85 }}
                                onClick={() => handleCopy(detailKey, resolveDetailValue(detailKey, value))}
                                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all"
                            >
                                {copiedKey === detailKey ? (
                                    <CheckCircle size={18} className="text-green-500" />
                                ) : (
                                    <Clipboard size={18} />
                                )}
                            </motion.button>
                        </div>
                    </div>
                ))}
            </motion.div>
        </div>
    );
};

export default BankDetails;
